import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useSellerFormattedDate } from '../../../../hooks/use-datetime-formats';
import { DateService } from '../../../../services/date-service';
import { ON_HOLD } from '../../../../services/api/order-status-from-view-service';

import style from '../order-summary.module.less';

const messages = defineMessages({
  orderDate: {
    description: 'orderDrawer.orderDate',
    defaultMessage: 'Order Date',
    id: 'Rd94BZ',
  },
  paidDate: {
    description: 'orderDrawer.paidDate',
    defaultMessage: 'Paid Date',
    id: 'LX1iPL',
  },
  shipBy: {
    description: 'orderDrawer.shipBy',
    defaultMessage: 'Ship By',
    id: '3mY0P2',
  },
  holdUntil: {
    description: 'orderDrawer.holdUntil',
    defaultMessage: 'Hold Until',
    id: 'yv1i81',
  },
  holdIndefinitely: {
    description: 'Text displayed when an order is on hold indefinitely with no end date',
    defaultMessage: 'No end date',
    id: 'ffVVY+',
  },
});

const DateColumn = ({ dateValueUtc = null, label = '', isHoldIndefinitely = false }) => {
  const userFriendlyDate = useSellerFormattedDate(dateValueUtc);

  const getContent = () => {
    if (isHoldIndefinitely) {
      return <FormattedMessage {...messages.holdIndefinitely} />;
    }
    if (dateValueUtc) {
      return userFriendlyDate;
    }
    return <span>&mdash;</span>;
  };

  return (
    <div className={style['detail-line']}>
      <div className={style.descriptor}>{label}</div>
      <div className={style.content}>{getContent()}</div>
    </div>
  );
};

DateColumn.propTypes = {
  dateValueUtc: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  isHoldIndefinitely: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
};

const DateDetailsComponent = ({ orderDateTime, orderGridStatus = '', paidDateTime, shipByDateTime = '', holdUntilDateTime = '' }) => {
  const isOnHold = orderGridStatus === ON_HOLD;
  const isEmptyDate = DateService.isEmptyDate(holdUntilDateTime);
  const isMaxDate = DateService.isMaxDate(holdUntilDateTime);
  const isOnHoldIndefinitely = isOnHold && (isEmptyDate || isMaxDate);

  return (
    <div>
      <div className={style['section-title']}>
        <FormattedMessage {...messages.orderDate} />
      </div>
      <DateColumn dateValueUtc={orderDateTime} label={<FormattedMessage {...messages.orderDate} />} />
      <DateColumn dateValueUtc={paidDateTime} label={<FormattedMessage {...messages.paidDate} />} />
      <DateColumn dateValueUtc={shipByDateTime} label={<FormattedMessage {...messages.shipBy} />} />
      <DateColumn
        dateValueUtc={holdUntilDateTime}
        isHoldIndefinitely={isOnHoldIndefinitely}
        label={<FormattedMessage {...messages.holdUntil} />}
      />
    </div>
  );
};

export const DateDetails = React.memo(DateDetailsComponent);

DateDetails.propTypes = {
  holdUntilDateTime: PropTypes.string,
  orderDateTime: PropTypes.string.isRequired,
  orderGridStatus: PropTypes.string,
  paidDateTime: PropTypes.string.isRequired,
  shipByDateTime: PropTypes.string,
};
